(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const live = (message) => { const region = qs('#MDLiveRegion'); if (region) region.textContent = message; };
  const escapeKey = (event) => event.key === 'Escape';

  function activateFocusTrap(container) {
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', container).filter((item) => !item.hidden && item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }

  function lockPage(locked) {
    document.body.classList.toggle('drawer-open', locked);
  }

  class MDMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('[data-menu-panel]', this);
      this.backdrop = qs('[data-menu-backdrop]', this);
      this.openers = qsa('[data-menu-open]');
      this.closeButton = qs('[data-menu-close]', this);
      this.openers.forEach((button) => button.addEventListener('click', () => this.open(button)));
      this.closeButton?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => { if (escapeKey(event) && this.panel?.classList.contains('is-open')) this.close(); });
    }
    open(trigger) {
      this.trigger = trigger;
      this.panel?.classList.add('is-open');
      this.backdrop?.classList.add('is-open');
      this.panel?.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute('aria-expanded', 'true');
      lockPage(true);
      this.releaseTrap?.();
      this.releaseTrap = activateFocusTrap(this.panel);
      this.closeButton?.focus();
    }
    close() {
      this.panel?.classList.remove('is-open');
      this.backdrop?.classList.remove('is-open');
      this.panel?.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');
      lockPage(false);
      this.releaseTrap?.();
      this.trigger?.focus();
    }
  }

  class MDSearchDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('[data-search-panel]', this);
      this.backdrop = qs('[data-search-backdrop]', this);
      this.input = qs('[data-predictive-input]', this);
      this.results = qs('[data-predictive-results]', this);
      this.openers = qsa('[data-search-open]');
      this.closeButton = qs('[data-search-close]', this);
      this.openers.forEach((button) => button.addEventListener('click', () => this.open(button)));
      this.closeButton?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => { if (escapeKey(event) && this.panel?.classList.contains('is-open')) this.close(); });
      let timer;
      this.input?.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 220);
      });
    }
    open(trigger) {
      this.trigger = trigger;
      this.panel?.classList.add('is-open');
      this.backdrop?.classList.add('is-open');
      this.panel?.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute('aria-expanded', 'true');
      lockPage(true);
      this.releaseTrap?.();
      this.releaseTrap = activateFocusTrap(this.panel);
      setTimeout(() => this.input?.focus(), 80);
    }
    close() {
      this.panel?.classList.remove('is-open');
      this.backdrop?.classList.remove('is-open');
      this.panel?.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');
      lockPage(false);
      this.releaseTrap?.();
      this.trigger?.focus();
    }
    async search(term) {
      if (!this.results) return;
      if (term.length < 2) { this.results.innerHTML = ''; this.input?.setAttribute('aria-expanded', 'false'); return; }
      this.results.setAttribute('aria-busy', 'true');
      try {
        const endpoint = `${window.mdRoutes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,page,article&resources[limit]=8&section_id=predictive-search`;
        const html = await fetch(endpoint).then((response) => {
          if (!response.ok) throw new Error('search');
          return response.text();
        });
        this.results.innerHTML = html;
        this.input?.setAttribute('aria-expanded', 'true');
      } catch (_) {
        this.results.innerHTML = `<p>${window.mdStrings.error}</p>`;
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  class MDCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('[data-cart-panel]', this);
      this.backdrop = qs('[data-cart-backdrop]', this);
      this.closeButton = qs('[data-cart-close]', this);
      qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', (event) => { event.preventDefault(); this.open(button); }));
      this.closeButton?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => { if (escapeKey(event) && this.panel?.classList.contains('is-open')) this.close(); });
      this.addEventListener('click', (event) => this.changeLine(event));
    }
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.panel?.classList.add('is-open');
      this.backdrop?.classList.add('is-open');
      this.panel?.setAttribute('aria-hidden', 'false');
      lockPage(true);
      this.releaseTrap?.();
      this.releaseTrap = activateFocusTrap(this.panel);
      this.closeButton?.focus();
    }
    close() {
      this.panel?.classList.remove('is-open');
      this.backdrop?.classList.remove('is-open');
      this.panel?.setAttribute('aria-hidden', 'true');
      lockPage(false);
      this.releaseTrap?.();
      this.trigger?.focus?.();
    }
    setLoading(loading) { this.classList.toggle('is-loading', loading); }
    async refresh(openAfter = false) {
      this.setLoading(true);
      try {
        const joiner = window.location.search ? '&' : '?';
        const html = await fetch(`${window.location.pathname}${window.location.search}${joiner}section_id=cart-drawer`).then((response) => response.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const replacement = qs('cart-drawer', doc);
        if (replacement) this.innerHTML = replacement.innerHTML;
        this.panel = qs('[data-cart-panel]', this);
        this.backdrop = qs('[data-cart-backdrop]', this);
        this.closeButton = qs('[data-cart-close]', this);
        this.closeButton?.addEventListener('click', () => this.close());
        this.backdrop?.addEventListener('click', () => this.close());
        await updateCartCount();
        if (openAfter) this.open();
      } finally { this.setLoading(false); }
    }
    async changeLine(event) {
      const closeControl = event.target.closest('[data-cart-close]');
      if (closeControl) { event.preventDefault(); this.close(); return; }
      const button = event.target.closest('[data-cart-change]');
      if (!button) return;
      event.preventDefault();
      this.setLoading(true);
      try {
        const response = await fetch(`${window.mdRoutes.cartChange}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(button.dataset.line), quantity: Number(button.dataset.quantity) })
        });
        if (!response.ok) throw new Error('cart');
        await this.refresh();
        live(window.mdStrings.added);
      } catch (_) { live(window.mdStrings.error); this.setLoading(false); }
    }
  }

  async function updateCartCount() {
    try {
      const cart = await fetch(`${window.mdRoutes.root}cart.js`).then((response) => response.json());
      qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; node.hidden = cart.item_count === 0; });
      return cart;
    } catch (_) { return null; }
  }

  async function addToCart(form, submitter) {
    const drawer = qs('cart-drawer');
    const error = qs('[data-product-error]', form);
    submitter?.setAttribute('aria-disabled', 'true');
    submitter?.classList.add('is-loading');
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${window.mdRoutes.cartAdd}.js`, {
        method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || window.mdStrings.error);
      await drawer?.refresh(true);
      live(window.mdStrings.added);
    } catch (errorValue) {
      if (error) { error.textContent = errorValue.message; error.hidden = false; }
      live(errorValue.message || window.mdStrings.error);
    } finally {
      submitter?.removeAttribute('aria-disabled');
      submitter?.classList.remove('is-loading');
    }
  }

  function syncSelectedVariant(select) {
    const section = select.closest('[data-product-section]');
    const option = select.options[select.selectedIndex];
    if (!section || !option) return;

    const available = option.dataset.variantAvailable === 'true';
    const price = option.dataset.variantPrice || '';
    const compareAt = option.dataset.variantCompare || '';
    const sku = option.dataset.variantSku || '';
    const priceWrapper = qs('[data-product-price]', section);
    const currentPrice = qs('[data-variant-price]', section);
    const comparePrice = qs('[data-variant-compare]', section);
    const skuWrapper = qs('[data-product-sku]', section);
    const skuValue = qs('[data-sku]', section);
    const submit = qs('[data-product-submit]', section);
    const stickySubmit = qs('[data-sticky-submit]', section);
    const stickyPrice = qs('[data-sticky-price]', section);

    if (currentPrice) currentPrice.textContent = price;
    if (stickyPrice) stickyPrice.textContent = price;
    if (comparePrice) {
      comparePrice.textContent = compareAt;
      comparePrice.hidden = !compareAt;
    }
    priceWrapper?.classList.toggle('price--sale', Boolean(compareAt));
    if (skuValue) skuValue.textContent = sku;
    if (skuWrapper) skuWrapper.hidden = !sku;

    [submit, stickySubmit].forEach((button) => {
      if (!button) return;
      button.disabled = !available;
      button.textContent = available ? window.mdStrings.addToCart : window.mdStrings.soldOut;
    });

    if (option.value) {
      const browserUrl = new URL(window.location.href);
      browserUrl.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', browserUrl);
    }
  }

  function initReveals(root = document) {
    const items = qsa('[data-reveal]:not(.is-visible)', root);
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { items.forEach((item) => item.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } });
    }, { threshold: .12, rootMargin: '0px 0px -30px' });
    items.forEach((item, index) => { item.style.transitionDelay = `${Math.min(index % 6, 5) * 55}ms`; observer.observe(item); });
  }

  function respectReducedMotion(root = document) {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    qsa('video[autoplay]', root).forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  function initVideoControls(root = document) {
    qsa('[data-video-toggle]:not([data-video-control-ready])', root).forEach((button) => {
      const container = button.closest('.hero-editorial,.video-banner,.editorial-split');
      if (!container) return;
      const video = qs('video', container);
      if (!video) return;
      button.dataset.videoControlReady = 'true';
      const label = qs('[data-video-toggle-label]', button);
      const sync = () => {
        button.classList.toggle('is-paused', video.paused);
        button.setAttribute('aria-label', video.paused ? 'Пусни видеото' : 'Спри видеото');
        if (label) label.textContent = video.paused ? 'ПУСНИ' : 'ПАУЗА';
      };
      button.addEventListener('click', () => {
        if (video.paused) video.play().catch(() => sync());
        else video.pause();
      });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      sync();
    });
  }

  class MDProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const replacement = qs('product-recommendations', doc);
          if (replacement?.innerHTML.trim()) {
            this.innerHTML = replacement.innerHTML;
            initReveals(this);
          }
        })
        .catch(() => {});
    }
  }

  customElements.define('md-menu-drawer', MDMenuDrawer);
  customElements.define('md-search-drawer', MDSearchDrawer);
  customElements.define('cart-drawer', MDCartDrawer);
  customElements.define('product-recommendations', MDProductRecommendations);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || form.dataset.cartType !== 'drawer') return;
    event.preventDefault();
    addToCart(form, event.submitter);
  });

  document.addEventListener('change', (event) => {
    const variantSelect = event.target.closest('[data-variant-select]');
    if (variantSelect) syncSelectedVariant(variantSelect);
  });

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const wrapper = quantityButton.closest('.quantity');
      const input = qs('input[type="number"]', wrapper);
      if (!input) return;
      const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(quantityButton.dataset.quantityChange));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const filterButton = event.target.closest('[data-filters-toggle]');
    if (filterButton) {
      const facets = qs('[data-facets]');
      facets?.classList.toggle('is-open');
      filterButton.setAttribute('aria-expanded', facets?.classList.contains('is-open') ? 'true' : 'false');
    }
    const addressToggle = event.target.closest('[data-address-toggle]');
    if (addressToggle) {
      const target = qs(`#${CSS.escape(addressToggle.dataset.addressToggle)}`);
      target?.classList.toggle('is-open');
    }
    const addressDelete = event.target.closest('[data-address-delete]');
    if (addressDelete && confirm(addressDelete.dataset.confirm)) {
      Shopify.postLink(addressDelete.dataset.addressDelete, { parameters: { _method: 'delete' } });
    }
    const stickyButton = event.target.closest('[data-sticky-submit]');
    if (stickyButton) qs('[data-product-form] [type="submit"]')?.click();
  });

  const header = qs('[data-site-header]');
  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  const stickyAtc = qs('[data-mobile-sticky-atc]');
  const productSubmit = qs('[data-product-submit]');
  if (stickyAtc && productSubmit && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => stickyAtc.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 }).observe(productSubmit);
  }

  document.addEventListener('shopify:cart:lines-update', () => qs('cart-drawer')?.refresh());
  document.addEventListener('shopify:section:load', (event) => {
    respectReducedMotion(event.target);
    initReveals(event.target);
    initVideoControls(event.target);
  });
  respectReducedMotion();
  initVideoControls();
  initReveals();
})();
